import type { Agency, Device, Merchant, MerchantStore } from '../../../common/types';
import {
  useGetDevicesForAgencyQuery,
  useGetDevicesForMerchantQuery,
  useGetDevicesForStoreQuery,
  useGetDevicesQuery
} from '../device.api-slice';
import AddOrEditApiConfig from '../../stores/components/DefaultApiConfig/AddOrEditApiConfig';
import ApproveDevice from '../components/ApproveDevice';
import Breadcrumb from '../../../components/Breadcrumb';
import DeleteDevice from '../components/DeleteDevice';
import EditDevice from '../components/EditDevice';
import { Link } from 'react-router-dom';
import ListDevices from '../components/ListDevices';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { OwnerType } from '../../../common/constants';
import Pagination from '../../../components/Pagination';
import SearchDevice from '../components/SearchDevice';
import { skipToken } from '@reduxjs/toolkit/query';
import SuccessMessage from '../../../components/success-message';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
  scope?: 'all' | 'agency' | 'merchant' | 'merchantStore';
  filter?: { pending: boolean };
  agency?: Agency;
  merchant?: Merchant;
  merchantStore?: MerchantStore;
}

const ListDevicesPage: React.FC<IProps> = ({ filter, agency, merchant, merchantStore, scope = 'all' }) => {
  const [editingItem, setEditingItem] = useState<Device | null>(null);
  const [deletingItem, setDeletingItem] = useState<Device | null>(null);
  const [approvingItem, setApprovingItem] = useState<Device | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openApproveModal, setOpenApproveModal] = useState<boolean>(false);
  const [openApiConfigModal, setOpenApiConfigModal] = useState<boolean>(false);

  const [page, setPage] = useState<number>(1);
  const [term, setTerm] = useState<string>('');
  const { t } = useTranslation();

  const { data: devices, isLoading } =
    scope === 'agency'
      ? useGetDevicesForAgencyQuery(agency != null ? { page, term, id: agency.id } : skipToken)
      : scope === 'merchant'
      ? useGetDevicesForMerchantQuery(merchant != null ? { page, term, id: merchant.id } : skipToken)
      : scope === 'merchantStore'
      ? useGetDevicesForStoreQuery(merchantStore != null ? { page, term, id: merchantStore.id } : skipToken)
      : useGetDevicesQuery({ page, term, pendingOnly: Boolean(filter?.pending) });

  const pageTitle =
    scope === 'agency' && agency != null
      ? `List of Devices for ${agency?.name}`
      : scope === 'merchant' && merchant != null
      ? `List of Devices for ${merchant?.name}`
      : scope === 'merchantStore' && merchantStore != null
      ? `List of Devices for ${merchantStore?.name}`
      : filter?.pending === true
      ? 'List of Pending Devices'
      : 'List of Devices';

  const handleSearch = (term: string): void => {
    setTerm(term);
  };

  const toggleEditModal = (): void => {
    if (openEditModal) {
      setEditingItem(null);
    }
    setOpenEditModal(!openEditModal);
  };

  const toggleDeleteModal = (): void => {
    if (openDeleteModal) {
      setDeletingItem(null);
    }
    setOpenDeleteModal(!openDeleteModal);
  };

  const toggleApproveModal = (): void => {
    if (openApproveModal) {
      setApprovingItem(null);
    }
    setOpenApproveModal(!openApproveModal);
  };

  const toggleApiConfigModal = (): void => {
    if (openApiConfigModal) {
      setSelectedDevice(null);
    }
    setOpenApiConfigModal(!openApiConfigModal);
  };

  const handleSelectDevice = (item: Device | null): void => {
    setSelectedDevice(item);
  };

  const handleEditingItem = (item: Device | null): void => {
    setEditingItem(item);
  };

  const handleDeletingItem = (item: Device | null): void => {
    setDeletingItem(item);
  };

  const handleApprovingItem = (item: Device | null): void => {
    setApprovingItem(item);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div className="pb-4">
        <h1>{t('devices.list.title')}</h1>
        <Breadcrumb
          items={[{ label: t('common.adminBreadcrumbLabel') }, { label: t('devices.list.breadcrumbLabel') }]}
        />
      </div>
      <div className="w-full flex justify-between items-center">
        <h1>{pageTitle}</h1>
        <div>
          <SearchDevice handleSearch={handleSearch} />
          {scope === 'all' && filter?.pending === true && (
            <Link to="/admin/devices">
              <button className="text-gray ml-4 px-5 py-2 rounded-lg bg-gray-50">List Devices</button>
            </Link>
          )}
          {scope === 'all' && filter?.pending !== true && (
            <Link to="/admin/devices/pending-requests">
              <button className="text-gray ml-4 px-5 py-2 rounded-lg bg-gray-50">Pending Devices</button>
            </Link>
          )}
        </div>
      </div>
      {devices?.data != null && (
        <>
          {selectedDevice != null && (
            <AddOrEditApiConfig
              ownerType={OwnerType.DEVICE}
              ownerEntity={selectedDevice}
              isOpen={openApiConfigModal}
              toggleModal={toggleApiConfigModal}
            />
          )}
          <EditDevice isOpen={openEditModal} toggleEditModal={toggleEditModal} item={editingItem} />

          <DeleteDevice isOpen={openDeleteModal} toggleDeleteModal={toggleDeleteModal} item={deletingItem} />
          <ApproveDevice isOpen={openApproveModal} toggleApproveModal={toggleApproveModal} item={approvingItem} />
          {devices?.data == null ||
            (devices.data.length === 0 && <SuccessMessage message={t('devices.list.empty')} type="error" />)}
          {devices?.data != null && devices?.data.length > 0 && (
            <>
              <ListDevices
                items={devices.data}
                pageType={filter?.pending === true ? 'pending' : 'confirmed'}
                toggleEditModal={toggleEditModal}
                toggleDeleteModal={toggleDeleteModal}
                toggleApproveModal={toggleApproveModal}
                toggleApiConfigModal={toggleApiConfigModal}
                handleEditingItem={handleEditingItem}
                handleDeletingItem={handleDeletingItem}
                handleApprovingItem={handleApprovingItem}
                handleSelectItem={handleSelectDevice}
              />
              <Pagination pageData={devices.meta} handlePageChange={setPage} />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ListDevicesPage;
